import { useEffect, useState } from 'react'
import dot from 'dot-prop-immutable'
import cloneDeep from 'lodash/cloneDeep'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import { useAppSelector } from 'reduxTyped'
import { useRefCallback } from '@manychat/manyui'

import { FilterSource } from 'common/filter/constants'
import { handleFilterAnalyticsData } from 'common/filter/filterAnalytics'
import { AnyFilter, FilterCondition, FilterSourceType } from 'common/filter/interfaces'
import { parseFilter, processFilter } from 'common/filter/models/AudienceFilter/adapter'
import { UNARY_OPERATORS } from 'common/filter/models/AudienceFilter/constants'
import { getDefaultFilter } from 'common/filter/models/AudienceFilter/defaults'
import * as helpers from 'common/filter/models/AudienceFilter/helpers'
import { getSystemFilterFields } from 'common/filter/selectors/filterFields'
import { usePrevious } from 'utils/commonHooks'
import { analyticsService } from 'utils/services/analytics'

import { FilterContextValue } from './types'

interface UseFilterInternalOptions<FilterType extends AnyFilter> {
  source: FilterSourceType
  isBuilder?: boolean
  onChange?: (filter: Nullable<FilterType>) => void
  initial: Nullable<FilterType>
  defaultFilters?: FilterType
  nodeId?: string
}

/**
 * Do not use this hook directly.
 * Use `useFilter` instead.
 */
export function useFilterInternal<FilterType extends AnyFilter>(
  options: UseFilterInternalOptions<FilterType>,
) {
  const systemFields = useAppSelector(getSystemFilterFields)
  const fixFilter = useRefCallback((filter: Nullable<FilterType>, defaultFilters?: FilterType) => {
    const filterGroupWithoutEmpty =
      filter && filter.groups && cloneDeep(filter.groups.filter((g) => g.items.length))
    filterGroupWithoutEmpty?.forEach((group) => group?.items.map((item) => delete item._oid))
    const defaultFilterGroupWithoutEmpty =
      defaultFilters && cloneDeep(defaultFilters.groups.filter((g) => g.items.length))

    const isFilterEqualDefault =
      defaultFilterGroupWithoutEmpty &&
      isEqual(filterGroupWithoutEmpty, defaultFilterGroupWithoutEmpty)

    return parseFilter(
      !isEmpty(filter) && !isFilterEqualDefault && filter
        ? filter
        : (getDefaultFilter(defaultFilters) as AnyFilter),
      systemFields,
    )
  })

  const { initial } = options
  const [filter, setFilter] = useState(() => fixFilter(initial, options.defaultFilters))

  const { source } = options
  const emitChange = useRefCallback((data: AnyFilter, action: string, force = false) => {
    setFilter(data)

    const next = processFilter(helpers.hasConditions(data) ? data : null)

    if (source !== FilterSource.LIVECHAT && action !== 'delete') {
      handleFilterAnalyticsData(next, source)
    }

    if (
      next &&
      next.groups
        .reduce((items, group) => items.concat(group.items), [] as FilterCondition[])
        .filter((item) => item.is_formula).length
    ) {
      analyticsService.sendEvent('FILTER.CUF_FORMULA_CONDITION_USED')
    }

    if (action !== 'create' || force) {
      options.onChange?.(next as FilterType)
    }
  })

  const changeOperator: FilterContextValue['changeOperator'] = useRefCallback(
    ({ operator, groupIndex }) => {
      emitChange(dot.set(filter, `groups.${groupIndex}.operator`, operator), 'operator')
    },
  )

  const { nodeId } = options
  const createCondition: FilterContextValue['createCondition'] = ({ groupIndex, field }) => {
    const condition = helpers.createCondition(field, null, null, { isBuilder: Boolean(nodeId) })

    emitChange(
      helpers.pushCondition(filter, groupIndex, condition),
      'create',
      UNARY_OPERATORS.includes(condition.operator as string),
    )
  }

  const changeCondition: FilterContextValue['changeCondition'] = ({
    groupIndex,
    _oid,
    condition: data,
  }) => {
    const index = filter.groups[groupIndex].items.findIndex((i) => i._oid === _oid)
    let res = dot.merge(filter, `groups.${groupIndex}.items.${index}`, data)
    const condition = res.groups[groupIndex].items[index]

    if (
      (condition.value === null || !`${condition.value}`) &&
      !UNARY_OPERATORS.includes(condition.operator)
    ) {
      res = helpers.removeCondition(res, groupIndex, index)
    }
    emitChange(res, 'update')
  }

  const removeCondition: FilterContextValue['removeCondition'] = useRefCallback(
    ({ groupIndex, _oid }) => {
      const index = filter.groups[groupIndex].items.findIndex((idx) => idx._oid === _oid)

      emitChange(helpers.removeCondition(filter, groupIndex, index), 'delete')
    },
  )

  const prevInitial = usePrevious(initial)
  useEffect(() => {
    if (!isEqual(initial, prevInitial)) {
      setFilter(fixFilter(initial, options.defaultFilters))
    }
    // should be same as prev componentDidUpdate
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, prevInitial, fixFilter, setFilter])

  return {
    value: filter,
    removeCondition,
    changeCondition,
    createCondition,
    changeOperator,
  }
}
